import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { parseRouteId } from '../../utils/routeId';
import { ContractStatusBadge } from '../../components/ui/StatusBadge';
import { showAlert, showConfirm, showPrompt, reportedError } from '../../utils/alert';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import Layout from '../../components/ui/Layout';
import { rawApi as api } from '../../utils/apiWithLogging';
import rentalService, {
  OutstandingReturns,
  CancellationFinalizeStatus,
} from '../../services/rentalService';
import { cn } from '../../utils/cn';
import { usePermission } from '../../hooks/usePermission';
import AnyDeskInlineEditor from '../../components/remoteAccess/AnyDeskInlineEditor';
import remoteAccessService, { type RemoteAccessUpdate } from '../../services/remoteAccessService';
import { companyDetailUrl } from '../../utils/companyLinks';
import { siteListByCompanyUrl, siteDetailUrl } from '../../utils/siteLinks';
import { getRentalActions, RENTAL_ACTION_HINTS } from '../../utils/rentalActions';
import { getRentalStatusDisplay } from '../../utils/rentalStatusLabel';
import { useApproveRental } from '../../hooks/mutations/useRentalMutations';
import ReturnEquipmentModal from '../../components/rental/ReturnEquipmentModal';
import CancelGoodsCheckModal from '../../components/rental/CancelGoodsCheckModal';
import BillingEndDateInlineEditor from '../../components/rental/BillingEndDateInlineEditor';
import PrepaidUntilBadge from '../../components/rental/PrepaidUntilBadge';
import RentalNotesSection from '../../components/rental/RentalNotesSection';
import ContractHistorySection from '../../components/rental/ContractHistorySection';
import MaintenanceHistorySection from '../../components/rental/MaintenanceHistorySection';
import DeviceReplacementModal from '../../components/rental/DeviceReplacementModal';
import DeviceReplacementHistory from '../../components/rental/DeviceReplacementHistory';
import DeviceMovementModal from '../../components/rental/DeviceMovementModal';
import DeviceMovementHistorySection from '../../components/rental/DeviceMovementHistorySection';
import AccessoryRequestHistorySection from '../../components/rental/AccessoryRequestHistorySection';
import RentalStockIssueSection from '../../components/stock/RentalStockIssueSection';
import RentalPreparationJobSection from '../../components/rental/RentalPreparationJobSection';
import RentalQuotationSection from '../../components/rental/RentalQuotationSection';
import RentalReturnAndCancellationSection from '../../components/rental/RentalReturnAndCancellationSection';
import RentalExtensionModal from '../../components/rental/RentalExtensionModal';
import RentalCancellationFlowModal from '../../components/rental/RentalCancellationFlowModal';
import DepositStatusBadge from '../../components/rental/DepositStatusBadge';
import DepositReturnModal from '../../components/rental/DepositReturnModal';
import DepositMarkPaidModal from '../../components/rental/DepositMarkPaidModal';
import DepositInvoiceBanner from '../../components/rental/DepositInvoiceBanner';
import ReceiveConditionModal, { ReceivableCondition } from '../../components/rental/ReceiveConditionModal';
import FinalizeCancellationModal from '../../components/rental/FinalizeCancellationModal';
import depositService, {
  DepositSummary,
  RentalItemDepositFields,
  canMarkDepositPaid,
  canReturnDeposit,
} from '../../services/depositService';
import { useReopenDeposit, useUnholdDeposit } from '../../hooks/mutations/useDepositMutations';
import ActionDropdown from '../../components/ui/ActionDropdown';
import LoadingButton from '../../components/ui/LoadingButton';
// Invoice/Payment modals removed - will be replaced with Company-level system
// import InvoiceListModal from '../../components/rental/InvoiceListModal';
// import PaymentListModal from '../../components/rental/PaymentListModal';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { toIctInstant } from '../../utils/dateUtils';
import {
  RiAlertLine,
  RiFileLine,
  RiBuilding2Line,
  RiMapPinLine,
  RiDeviceLine,
  RiSimCardLine,
  RiFileListLine,
  RiHistoryLine,
  RiCalendarLine,
  RiMoneyDollarCircleLine,
  RiFileTextLine,
  RiEditLine,
  RiArrowGoBackLine,
  RiShieldCheckLine,
  RiTimeLine,
  RiUserLine,
  RiExchangeLine,
  RiEyeLine,
  RiRefreshLine,
  RiRemoteControlLine,
  RiTruckLine,
  RiCheckLine,
  RiCalendarLine as RiCalendarAddLine,
  RiCloseCircleLine,
  RiDeleteBinLine,
  RiMore2Fill,
  RiCheckboxCircleLine,
  // RiFileList3Line, // Removed - was used for Invoice button
  // RiWallet3Line, // Removed - was used for Payment button
} from 'react-icons/ri';

// Enhanced rental interface with rental_items support
// ฟิลด์มัดจำ (deposit_status/paid/returned + Deposit v2 hold/refund/computed) มาจาก RentalItemDepositFields
interface RentalItem extends RentalItemDepositFields {
  id: number;
  product_id: number | null;
  product_name?: string | null;
  product?: { id: number; name: string } | null;
  device_id?: number;
  device_serial?: string;
  device?: {
    id: number;
    code: string;
    serial_number: string;
    model?: string;
    status: string;
    device_type?: { id: number; name: string };
    device_type_id?: number;
    device_model?: { id: number; name: string };
    device_model_id?: number;
  };
  sim_id?: number | null;
  // API ส่ง sim object (id, phone_number, operator) มาคู่กับ sim_id — null เมื่อรายการไม่มี SIM
  sim?: {
    id: number;
    phone_number: string;
    operator: string;
  } | null;
  quantity: number;
  rental_rate: number;
  rental_period_days: number;
  total_amount?: number; // ยอดรวมของแถว (qty × rate × ระยะเวลา) จาก API — ใช้แสดงคอลัมน์ "รวม"
  is_software_only?: boolean; // เช่า Software อย่างเดียว (ลูกค้าใช้เครื่องตัวเอง)
  // Remote Access (AnyDesk) — ค่าเดียวของรอบเช่า แก้ผ่าน remoteAccessService
  anydesk_id?: string | null;
  remote_access_notes?: string | null;
}

interface Rental {
  id: number;
  rental_number: string;
  code?: string; // For backward compatibility
  rental_type?: string;
  rental_date: string;
  billing_start_date?: string; // วันที่เริ่มเก็บเงินจริง
  billing_end_date?: string;   // วันสิ้นสุดเก็บเงิน (single source of truth for proration)
  prepaid_until?: string | null; // ชำระล่วงหน้าถึงวันที่ (วางบิลข้ามรอบที่ครอบคลุม)
  prepaid_note?: string | null;
  expected_return_date?: string;
  actual_return_date?: string;
  end_date?: string;
  status: string;
  total_amount?: number;
  deposit_amount?: number;
  monthly_fee?: number;
  
  site_id: number;
  site_name?: string;
  site?: { id: number; name: string; address?: string };
  company_id?: number;
  company_name?: string;
  company?: { id: number; name: string };
  
  // Legacy single item fields
  product_id?: number;
  product_name?: string;
  device_id?: number;
  device_code?: string;
  sim_id?: number;
  sim_number?: string;
  
  // New rental_items array
  rental_items?: RentalItem[];
  
  contract_id?: number;
  contract_number?: string;
  contract?: { id: number; contract_number: string; status?: string };
  
  notes?: string;
  created_at: string;
  updated_at?: string;
  created_by_user_id?: number;
  approved_by_user_id?: number;
  // New fields for data migration
  delivery_date?: string;
}

interface ActivityLog {
  id: number;
  action: string;
  description?: string;
  created_at: string;
  user_name?: string;
  user_email?: string;
}

// P-07: จำนวน log ต่อหน้าของ timeline "ประวัติการดำเนินการ" (backend รับ limit 1-100)
const ACTIVITY_PAGE_SIZE = 20;

const RentalViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  // BUG-S2283-001: id ต้องเป็นจำนวนเต็มบวกล้วน ("487abc"/"1e3" เดิมเปิดรายการเช่าผิดตัว · "abc" ยิง /NaN)
  const rentalId = parseRouteId(id);
  const { canView, canEdit, canDelete, hasPermission } = usePermission();
  const approveMutation = useApproveRental();
  const [loading, setLoading] = useState(true);
  const [rental, setRental] = useState<Rental | null>(null);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  // P-07 (E2E v5 deposit/rental): timeline เดิมดึงแค่ 10 รายการแล้วตัดทิ้ง → log เก่าสุด เช่น "สร้างรายการเช่า"
  // หายจากหน้าจอเมื่อมี log > 10 · เปลี่ยนเป็น append pagination (โหลดหน้าถัดไปแล้วต่อท้าย)
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(1);
  const [loadingMoreLogs, setLoadingMoreLogs] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [selectedRentalItem, setSelectedRentalItem] = useState<RentalItem | null>(null);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showCancellationFlowModal, setShowCancellationFlowModal] = useState(false);
  // L1: "ของกลับแล้วหรือยัง" ต้องถามตรงจุดที่กดยกเลิก + ของค้างต้องปิดได้จากหน้านี้
  // (prod: หน้าติดตามการคืนอุปกรณ์ถูกเปิด 2 ครั้ง/90 วัน และไม่เคยมีใครปิดใบตามเก็บเลย)
  const [showCancelGoodsModal, setShowCancelGoodsModal] = useState(false);
  const [outstandingReturns, setOutstandingReturns] = useState<OutstandingReturns | null>(null);
  const [recordingReturns, setRecordingReturns] = useState(false);
  // Deposit v2: modal บันทึกคืน/คง + modal รับมัดจำ + สรุปมัดจำจาก /deposit-summary
  const [depositReturnItem, setDepositReturnItem] = useState<RentalItem | null>(null);
  const [depositMarkPaidItem, setDepositMarkPaidItem] = useState<RentalItem | null>(null);
  const [depositSummary, setDepositSummary] = useState<DepositSummary | null>(null);
  const unholdMutation = useUnholdDeposit();
  const reopenMutation = useReopenDeposit();
  // Invoice/Payment modal states removed - will be replaced with Company-level system
  // const [showInvoiceListModal, setShowInvoiceListModal] = useState(false);
  // const [showPaymentListModal, setShowPaymentListModal] = useState(false);
  // Fix ก (API_CONTRACT.md ก-1/ก-2, 20 ก.ย. 2026): สัญญา "รอการยกเลิก" ที่รับของคืนครบแล้ว —
  // เสนอ "ปิดใบแจ้งยกเลิก" ทันที (ของถูกล็อกอยู่จนกว่าจะปิด แม้สถานะจะขึ้น "พร้อมใช้งาน" แล้วก็ตาม)
  const [finalizeStatus, setFinalizeStatus] = useState<CancellationFinalizeStatus | null>(null);
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);

  // Permission check
  useEffect(() => {
    if (!canView('rentals')) {
      navigate('/rentals/list');
    }
  }, [canView, navigate]);

  useEffect(() => {
    if (rentalId === null) {
      // id ไม่ถูกต้อง → ไม่ยิง API · แสดง "ไม่พบข้อมูลการเช่า" (หน้าเดียวกับ rental ว่าง)
      setRental(null);
      setLoading(false);
      return;
    }
    reloadRentalView();
  }, [rentalId]);

  // ก-1: เช็คทุกครั้งที่ rental เปลี่ยน — loadRentalData() (ที่ reloadRentalView() เรียกอยู่แล้วหลัง
  // ทุก mutation ของหน้านี้) สร้าง object ใหม่เสมอ ผูก effect กับ `rental` จึงรีเฟรชสถานะนี้ตามไปเอง
  // โดยไม่ต้องเรียกซ้ำที่ทุกจุด mutation · เฉพาะสัญญา "รอการยกเลิก" เท่านั้น — สถานะอื่นไม่มี banner
  useEffect(() => {
    if (!rental || rental.status !== 'PENDING_CANCELLATION') {
      setFinalizeStatus(null);
      return;
    }
    let cancelled = false;
    rentalService.getFinalizeCancellation(rental.id)
      .then((status) => { if (!cancelled) setFinalizeStatus(status); })
      .catch((error) => {
        // ไม่ critical — ไม่มี banner ก็แค่ต้องปิดใบแจ้งยกเลิกทางเดิม (รอวันที่แจ้งไว้)
        console.error('Error loading finalize-cancellation status:', error);
        if (!cancelled) setFinalizeStatus(null);
      });
    return () => { cancelled = true; };
  }, [rental]);

  // สรุปมัดจำ (rentals.view) — ล้มเหลวไม่ critical: การ์ดการเงิน fallback ไป rental.deposit_amount
  const loadDepositSummary = async () => {
    if (rentalId === null) return;
    try {
      const summary = await depositService.getDepositSummary(rentalId);
      setDepositSummary(summary);
    } catch (error) {
      console.error('Error loading deposit summary:', error);
    }
  };

  // หลังทุก mutation ของหน้านี้: โหลด rental (badge/ปุ่ม), timeline, สรุปมัดจำ ใหม่พร้อมกัน —
  // ยกเลิก/ปิดงาน/อนุมัติ/คืนของ/ต่ออายุ/ย้าย ก็เปลี่ยนสถานะมัดจำ (PAID → รอคืน) จึงต้อง refresh การ์ดสรุปด้วย
  // (E2E Deposit v2 FAIL-minor-B-a: เดิม handler เหล่านั้นโหลดแค่ rental+timeline → การ์ดค้าง "ชำระแล้ว" จน reload)
  const reloadRentalView = () => {
    loadRentalData();
    // P-07: เรียกแบบไม่ส่ง page → กลับไปหน้า 1 เสมอ — ยอมรับได้ เพราะทุก mutation จะสร้าง log ใหม่
    // ที่อยู่บนสุดของ timeline (เรียงใหม่→เก่า) ผู้ใช้กด "ดูประวัติเพิ่ม" ต่อได้ถ้าต้องการรายการเก่า
    loadActivityLogs();
    loadDepositSummary();
    loadOutstandingReturns();
  };

  // AnyDesk รายการเช่า — ค่าเดียวที่ทุกหน้าจอแก้ (PUT /rentals/{id}/items/{item}/remote-access · rentals.remote_access)
  // แสดงเมื่อแถวมีเครื่อง หรือมีค่าอยู่แล้ว · Software อย่างเดียวที่ยังไม่มีค่าไม่ต้องโชว์ช่องว่าง
  const showAnydeskFor = (item: RentalItem): boolean =>
    !!(item.anydesk_id || item.remote_access_notes || (!item.is_software_only && (item.device_id || item.device)));

  const saveItemRemoteAccess = async (itemId: number, data: RemoteAccessUpdate) => {
    if (rentalId === null) return;
    await remoteAccessService.updateRentalItem(rentalId, itemId, data);
    reloadRentalView();
  };

  // ของที่ยังไม่ได้รับคืนของสัญญานี้ — โชว์เป็นการ์ดเตือน + ปุ่มบันทึกรับคืน
  const loadOutstandingReturns = async () => {
    if (rentalId === null) return;
    try {
      setOutstandingReturns(await rentalService.getOutstandingReturns(rentalId));
    } catch (error) {
      console.error('Error loading outstanding returns:', error);
    }
  };

  // บันทึก "รับคืนแล้ว" ให้ของที่ค้าง — ไม่เปลี่ยนสถานะสัญญา (ใช้ได้แม้สัญญายกเลิก/ปิดไปแล้ว)
  // 2026-09-08: เดิมส่ง equipment_condition: 'GOOD' ตายตัวเหมือนปุ่มที่หน้าติดตามการคืน
  // → ของชำรุดกลับเข้าคลังให้เช่าต่อโดยไม่มีใครเลือกได้ · ถามสภาพก่อนเสมอ ไม่มีค่าตั้งต้น
  const [recordReturnsOpen, setRecordReturnsOpen] = useState(false);

  const handleRecordReturns = () => {
    if (!rental) return;
    setRecordReturnsOpen(true);
  };

  const submitRecordReturns = async (condition: ReceivableCondition, notes?: string) => {
    if (!rental) return;
    setRecordingReturns(true);
    try {
      // BUG-S2191-001: ส่งเฉพาะรายการที่การ์ด "ยังไม่ได้รับคืน" แสดงอยู่ — ถ้าไม่ระบุ backend ตีความ
      // "ของกลับครบทั้งสัญญา" (ดีไซน์ของปุ่มยกเลิก/ปิดสัญญา) → บนสัญญาที่ยังใช้งานอยู่ เครื่องที่ลูกค้า
      // ยังถือถูกบันทึกว่ารับคืน + พร้อมใช้งาน ทั้งที่การ์ดบอกว่าค้างแค่ N รายการ
      const outstandingItemIds = Array.from(
        new Set((outstandingReturns?.items ?? []).map((it) => it.rental_item_id))
      );
      await rentalService.recordReturns(rental.id, {
        equipment_condition: condition,
        condition_notes: notes,
        ...(outstandingItemIds.length > 0 ? { returned_item_ids: outstandingItemIds } : {}),
      });
      setRecordReturnsOpen(false);
      showAlert('สำเร็จ', 'บันทึกรับคืนอุปกรณ์เรียบร้อยแล้ว', 'success');
      reloadRentalView();
      // ก-1 ข้อ 2 (API_CONTRACT.md "Where FE uses ก"): รับคืนของค้างรอบนี้อาจทำให้สัญญา "รอการยกเลิก"
      // ของกลับครบพอดี — เช็คซ้ำแล้วเปิดโมดัลปิดใบแจ้งยกเลิกให้เอง ไม่ต้องรอผู้ใช้กดปุ่มจาก banner
      if (rental.status === 'PENDING_CANCELLATION') {
        try {
          const freshStatus = await rentalService.getFinalizeCancellation(rental.id);
          setFinalizeStatus(freshStatus);
          if (freshStatus.eligible) {
            setFinalizeModalOpen(true);
          }
        } catch (error) {
          // ไม่ critical — banner ปกติจะรีเฟรชสถานะนี้เองจาก reloadRentalView() ด้านบนอยู่แล้ว
          console.error('Error checking finalize-cancellation eligibility:', error);
        }
      }
    } catch (error: any) {
      const msg = getApiErrorMessage(error, 'ไม่สามารถบันทึกรับคืนได้');
      reloadRentalView(); // KD3 S2357: สถานะของ/ใบตามเก็บอาจถูกเปลี่ยนจากที่อื่นแล้ว
      showAlert('เกิดข้อผิดพลาด', msg, 'error');
    } finally {
      setRecordingReturns(false);
    }
  };

  const loadRentalData = async () => {
    if (rentalId === null) return;
    try {
      setLoading(true);
      // ใช้ endpoint /edit เพื่อได้ข้อมูล rental_items ครบถ้วน
      const response = await api.get(`/rentals/${rentalId}/edit`);
      // Handle both code and rental_number fields
      const rentalData = response.data;
      if (!rentalData.code && rentalData.rental_number) {
        rentalData.code = rentalData.rental_number;
      }
      // คำนวณ monthly_fee จาก rental_items ถ้าไม่มีมาจาก API
      if (!rentalData.monthly_fee && rentalData.rental_items) {
        rentalData.monthly_fee = rentalData.rental_items.reduce((sum: number, item: any) => {
          return sum + (item.rental_rate || 0) * (item.quantity || 1);
        }, 0);
      }
      
      
      // Debug product information in rental items
      if (rentalData.rental_items) {
        rentalData.rental_items.forEach((item: any, index: number) => {
        });
      }
      setRental(rentalData);
    } catch (error) {
      console.error('Error loading rental data:', error);
      // BUG-S138-VIEW404MSG FIX: เดิม hardcode "ไม่สามารถโหลดข้อมูลการเช่าได้" ทับข้อความจริงจาก
      // backend — เปิดลิงก์ของรายการเช่าที่ถูกลบ/ไม่มีอยู่ (404 `ไม่พบรายการเช่าที่ระบุ`) ผู้ใช้จะ
      // เห็นว่า "โหลดไม่ได้" ซึ่งชวนให้ลองใหม่ ทั้งที่ความจริงคือรายการนั้นไม่มีแล้ว
      // (ตระกูลเดียวกับ BUG-FE-RETURN-MSG ที่แก้ไปแล้วในไฟล์นี้ · sibling ของ BUG-RAWERR-SIBLINGS)
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถโหลดข้อมูลการเช่าได้'), 'error');
      navigate('/rentals/list');
    } finally {
      setLoading(false);
    }
  };

  // P-07 (E2E v5 deposit/rental): append pagination — page 1 แทนที่ทั้งหมด · page > 1 ต่อท้ายรายการเดิม
  // โดย dedupe ตาม id (กัน offset เลื่อนเมื่อมี log ใหม่แทรกระหว่างที่ผู้ใช้กดโหลดหน้าถัดไป)
  // ระหว่างโหลดหน้าถัดไปใช้ loadingMoreLogs แยกจาก loadingLogs → spinner ใหญ่ไม่ทับรายการเดิม
  const loadActivityLogs = async (page: number = 1) => {
    if (rentalId === null) return;
    const isFirstPage = page <= 1;
    try {
      if (isFirstPage) setLoadingLogs(true);
      else setLoadingMoreLogs(true);
      // BUG-S2011-001 (KD3 S2011 FLOW-RENT-076, 2026-09-02): เดิมเรียก /activity-logs/ ซึ่ง SEC-007 บังคับ
      // user_id = me สำหรับผู้ที่ไม่มี activity_logs.view_all → Support/Manager เห็น "ไม่มี" ประวัติ
      // ทั้งที่ rental ถูกสร้าง/อนุมัติโดยคนอื่น · ใช้ endpoint ที่ scope ตาม entity (gate rentals.view) แทน
      const response = await api.get(`/rentals/${rentalId}/activities`, {
        params: { page, limit: ACTIVITY_PAGE_SIZE }
      });
      const items: ActivityLog[] = (response.data.items || []).map((a: any) => ({
        id: a.id,
        action: a.action,
        description: a.description,
        created_at: a.performed_at,
        user_name: a.performed_by,
        user_email: a.user_email ?? '',
      }));
      if (isFirstPage) {
        setActivityLogs(items);
        setActivityTotal(Number(response.data.total ?? items.length) || 0);
      } else {
        // ปุ่มถูก disable ระหว่างโหลด → activityLogs ใน closure คือรายการล่าสุดจริง
        const seen = new Set(activityLogs.map((l) => l.id));
        const merged = [...activityLogs, ...items.filter((l) => !seen.has(l.id))];
        setActivityLogs(merged);
        // หน้าถัดไปว่าง = ดึงครบแล้ว (total ที่โตขึ้นระหว่างทางคือ log ใหม่บนหน้า 1 ไม่ใช่ของเก่าที่ยังไม่โหลด)
        // → ซ่อนปุ่ม ไม่ให้กดแล้วไม่มีอะไรเกิดขึ้น
        setActivityTotal(items.length === 0 ? merged.length : Number(response.data.total ?? merged.length) || 0);
      }
      setActivityPage(page);
    } catch (error) {
      console.error('Error loading activity logs:', error);
      // Don't show error for activity logs, it's not critical
    } finally {
      if (isFirstPage) setLoadingLogs(false);
      else setLoadingMoreLogs(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config = getRentalStatusDisplay(status);

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${config.badgeColor}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    // R180: normalize naive-ICT backend datetime so timeZone conversion is correct on non-Bangkok browsers
    return new Date(toIctInstant(dateString)).toLocaleString('th-TH', {
      year: '2-digit',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Bangkok'
    });
  };

  const calculateTotalAmount = () => {
    return rental?.total_amount || 0;
  };

  const getActionLabel = (action: string) => {
    const actions: { [key: string]: string } = {
      'CREATE': 'สร้างรายการเช่า',
      'UPDATE': 'แก้ไขรายการเช่า',
      'DELETE': 'ลบรายการเช่า',
      'STATUS_CHANGE': 'เปลี่ยนสถานะ',
      'CONTRACT_CREATED': 'สร้างสัญญา',
      'CONTRACT_LINKED': 'เชื่อมโยงสัญญา',
      'RETURN': 'คืนอุปกรณ์',
      'RENEWAL': 'ต่อสัญญา',
      // KD3 S2011 FLOW-RENT-076: action ที่ backend บันทึกจริงแต่ไม่มี label (แสดงเป็นโค้ดดิบ)
      'APPROVE': 'อนุมัติการเช่า',
      'CANCEL': 'ยกเลิกการเช่า',
      'COMPLETE': 'ปิดการเช่า',
      'RESTORE': 'กู้คืนรายการเช่า',
      'SOFT_DELETE': 'ลบรายการเช่า',
      'EXTEND': 'ต่ออายุการเช่า',
      'TRANSFER': 'ย้ายโครงการ',
      'REPLACEMENT': 'เปลี่ยนอุปกรณ์',
      // Deposit v2 (PO 2026-09-02): action จาก /deposit/* + ระบบตั้ง PENDING_REFUND ตอน cancel/complete
      'DEPOSIT_PAID': 'บันทึกรับมัดจำ',
      'DEPOSIT_PENDING_REFUND': 'มัดจำรอคืน',
      'DEPOSIT_RETURN': 'บันทึกคืนมัดจำ',
      'DEPOSIT_HOLD': 'คงมัดจำไว้',
      'DEPOSIT_UNHOLD': 'ปลดการคงมัดจำ',
      'DEPOSIT_REOPEN': 'แก้ไขบันทึกคืนมัดจำ',
      // ทาง ค (21 ก.ย. 2026): เปิดใบแจ้งยกเลิกใหม่ — สัญญากลับเป็นรอการยกเลิก + มัดจำรอคืนกลับเป็นชำระแล้ว
      'UNCANCEL': 'เปิดใบแจ้งยกเลิกใหม่ (ถอนการยกเลิก)',
      'DEPOSIT_REINSTATE': 'มัดจำกลับเป็นชำระแล้ว',
      // FB-2026-0017: พิมพ์ใบส่งของจากหน้ารายการเช่า (backend services/delivery_note.ACTION_PRINT_DELIVERY_NOTE)
      'PRINT_DELIVERY_NOTE': 'พิมพ์ใบส่งของ'
    };
    return actions[action] || action;
  };

  // ===== Deposit v2 actions (ต้องมี rentals.deposit_manage — ปุ่มซ่อนถ้าไม่มี) =====
  const handleUnholdDeposit = (item: RentalItem) => {
    if (!rental) return;
    showConfirm(
      'ปลดการคงมัดจำ',
      `ปลดการคงมัดจำรายการ "${item.product_name || item.product?.name || `#${item.id}`}"? สถานะจะกลับเป็น "รอคืนมัดจำ" และเริ่มนับกำหนดคืนใหม่ตั้งแต่วันนี้`,
      async () => {
        try {
          await unholdMutation.mutateAsync({ rentalId: rental.id, itemId: item.id, data: {} });
          reloadRentalView();
        } catch (error: any) {
          const msg = getApiErrorMessage(error, 'ไม่สามารถปลดการคงมัดจำได้');
          reloadRentalView(); // KD3 S2357: สถานะมัดจำอาจถูกเปลี่ยนจากที่อื่นแล้ว
          showAlert('เกิดข้อผิดพลาด', msg, 'error');
          throw reportedError(msg);
        }
      },
      { confirmText: 'ปลดการคงมัดจำ', cancelText: 'ยกเลิก' }
    );
  };

  const handleReopenDeposit = async (item: RentalItem) => {
    if (!rental) return;
    // confirm + เหตุผลในกล่องเดียว: ข้อความอธิบายผล (ล้างข้อมูลคืน → กลับเป็นรอคืนมัดจำ) + ช่องเหตุผลบังคับ
    const reason = await showPrompt(
      'แก้ไขบันทึกคืนมัดจำ',
      `เปิดบันทึกคืนมัดจำรายการ "${item.product_name || item.product?.name || `#${item.id}`}" ใหม่? ข้อมูลการคืน (วันที่/วิธี/อ้างอิง) จะถูกล้าง และสถานะกลับเป็น "รอคืนมัดจำ" — กรุณาระบุเหตุผล`,
      { placeholder: 'เหตุผลที่ต้องแก้ไข เช่น บันทึกวันที่ผิด', required: true, inputType: 'textarea', submitText: 'เปิดบันทึกใหม่' }
    );
    if (!reason) return;
    try {
      await reopenMutation.mutateAsync({ rentalId: rental.id, itemId: item.id, data: { reason } });
      reloadRentalView();
    } catch (error: any) {
      reloadRentalView(); // KD3 S2357: สถานะมัดจำอาจถูกเปลี่ยนจากที่อื่นแล้ว
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถแก้ไขบันทึกคืนมัดจำได้'), 'error');
    }
  };

  /** ปุ่มมัดจำต่อรายการ — ใช้ทั้ง card (mobile) และ table (desktop); คืน null เมื่อไม่มีปุ่ม */
  const renderDepositActions = (item: RentalItem, compact: boolean) => {
    if (!rental || !actions.manageDeposit) return null;
    const isHold = item.deposit_status === 'PENDING_REFUND' && !!item.deposit_hold;
    const showReturn = canReturnDeposit(item, rental.status);
    const showUnhold = isHold;
    const showReopen = item.deposit_status === 'RETURNED';
    const showMarkPaid = canMarkDepositPaid(item, rental.status);
    if (!showReturn && !showUnhold && !showReopen && !showMarkPaid) return null;
    const btn = cn(
      'inline-flex items-center justify-center rounded-md font-medium transition-colors whitespace-nowrap',
      compact ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-xs min-h-[36px]'
    );
    return (
      <div className={cn('flex flex-wrap gap-1.5', compact ? 'justify-center mt-1.5' : 'mt-2')}>
        {showReturn && (
          <button
            type="button"
            onClick={() => setDepositReturnItem(item)}
            className={cn(btn, 'text-white bg-primary-600 hover:bg-primary-700')}
          >
            บันทึกการคืนมัดจำ
          </button>
        )}
        {showUnhold && (
          <button
            type="button"
            onClick={() => handleUnholdDeposit(item)}
            className={cn(btn, 'text-purple-700 bg-purple-100 hover:bg-purple-200')}
          >
            ปลดการคงมัดจำ
          </button>
        )}
        {showReopen && (
          <button
            type="button"
            onClick={() => handleReopenDeposit(item)}
            className={cn(btn, 'text-secondary-700 bg-secondary-100 hover:bg-secondary-200')}
          >
            แก้ไขบันทึกคืน
          </button>
        )}
        {showMarkPaid && (
          <button
            type="button"
            onClick={() => setDepositMarkPaidItem(item)}
            className={cn(btn, 'text-success-700 bg-success-100 hover:bg-success-200')}
          >
            บันทึกรับมัดจำ
          </button>
        )}
      </div>
    );
  };

  const handleReturnEquipment = async (returnData: any) => {
    try {
      await api.post(`/rentals/${rentalId}/return`, returnData);
      showAlert('สำเร็จ', 'บันทึกการคืนอุปกรณ์เรียบร้อยแล้ว', 'success');
      setShowReturnModal(false);
      // Reload data
      reloadRentalView();
    } catch (error: any) {
      console.error('Error returning equipment:', error);
      // BUG-FE-RETURN-MSG FIX: surface the backend's business-rule reason (e.g. BIZ-006
      // "…มีอุปกรณ์ N รายการที่ยังไม่ได้คืน: {รหัสเครื่อง} — ติ๊กรายการที่รับคืนแล้ว…") verbatim instead of
      // a generic message, so the user knows WHY the close was blocked.
      // E2E v6 PRE-1: ใช้ getApiErrorMessage (อ่าน KRP envelope + detail + network error) เหมือน handler อื่นในหน้านี้
      reloadRentalView(); // KD3 S2357: สถานะอาจถูกเปลี่ยนจากที่อื่นแล้ว
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถบันทึกการคืนอุปกรณ์ได้'), 'error');
      throw error;
    }
  };

  // BUG-S2204-001: รายการที่มีแต่ซิม (เช่า Software + ซิม) ต้องเปลี่ยนซิมได้ด้วย — เดิม gate แค่ item.device
  // ทำให้ไม่มีทางเปลี่ยนซิมจากจอเลย ทั้งที่ backend รับ SIM_ONLY และ modal ตั้งค่าเริ่มต้นเป็น SIM_ONLY ให้เอง
  const canReplaceItem = (item: RentalItem): boolean => !!(item.device || item.sim || item.sim_id);

  const handleOpenReplacementModal = (item: RentalItem) => {
    setSelectedRentalItem(item);
    setShowReplacementModal(true);
  };

  // เบอร์ SIM ต่อรายการ — ถ้า API ยังไม่ส่ง sim object มา แสดง "SIM #id" แทน (ไม่มี SIM → null)
  const getItemSimLabel = (item: RentalItem): string | null => {
    if (item.sim?.phone_number) return item.sim.phone_number;
    const simId = item.sim?.id ?? item.sim_id;
    return simId ? `SIM #${simId}` : null;
  };

  const handleReplacementSuccess = () => {
    // Reload data after successful replacement
    reloadRentalView();
  };

  const handleApprove = () => {
    if (!rental) return;
    showConfirm(
      'อนุมัติการเช่า',
      `อนุมัติและเริ่มสัญญาเช่า ${rental.code || rental.rental_number}? อุปกรณ์จะถูกจองและสถานะเปลี่ยนเป็น "เช่าใช้อยู่"`,
      async () => {
        try {
          await approveMutation.mutateAsync({ id: rental.id });
          reloadRentalView();
        } catch (error: any) {
          const msg = error?.response?.data?.error?.user_message || error?.response?.data?.detail || 'ไม่สามารถอนุมัติได้';
          // KD3 S2357: อีกคนอนุมัติ/ยกเลิกร่างไปก่อน → backend 400 "(สถานะปัจจุบัน: …)" แต่จอยังโชว์ "ร่าง" + ปุ่มอนุมัติ
          // → โหลดสัญญาใหม่ให้จอตรงกับข้อความ (ทุก action เปลี่ยนสถานะในหน้านี้ทำเหมือนกัน)
          reloadRentalView();
          // useMutationWithRefresh แสดง toast ไปแล้ว → ไม่ต้อง showAlert ซ้ำ (BUG-FE-TOAST-DUP)
          throw reportedError(msg);
        }
      },
      { confirmText: 'อนุมัติ', cancelText: 'ยกเลิก' }
    );
  };

  const handleVoidDraft = () => {
    if (!rental) return;
    showConfirm(
      'ยกเลิกร่าง',
      `ยกเลิกร่างการเช่า ${rental.code || rental.rental_number}? · ร่างยังไม่ได้จองอุปกรณ์เอง แต่ถ้าร่างนี้มาจากใบงานเตรียมเครื่อง อุปกรณ์ที่ใบงานกันไว้จะถูกปล่อยกลับคลังทันที`,
      async () => {
        try {
          await rentalService.updateRentalStatus(rental.id, 'CANCELLED');
          reloadRentalView();
        } catch (error: any) {
          // BUG-S2015-001 (KD3 S2015 NEG-RENT-006): อ่าน KRP envelope (error.user_message/message)
          // ก่อน — เดิมอ่านแค่ `.detail` (FastAPI default) ซึ่ง KRP ไม่ส่ง → ผู้ใช้เห็น fallback generic เสมอ
          const msg = getApiErrorMessage(error, 'ไม่สามารถยกเลิกได้');
          reloadRentalView(); // KD3 S2357: สถานะอาจถูกเปลี่ยนจากที่อื่นแล้ว
          showAlert('เกิดข้อผิดพลาด', msg, 'error');
          throw reportedError(msg);
        }
      },
      { isDanger: true, confirmText: 'ยกเลิกร่าง', cancelText: 'ปิด' }
    );
  };

  const handleCancelAction = (mode: string | null) => {
    if (!rental) return;
    if (mode === 'void') handleVoidDraft();
    else if (mode === 'continue') navigate(`/rentals/cancellation-tracking?rentalId=${rental.id}`);
    else setShowCancellationFlowModal(true); // 'flow'
  };

  const handleDeleteRental = () => {
    if (!rental) return;
    showConfirm(
      'ลบรายการเช่า',
      `ลบรายการเช่า ${rental.code || rental.rental_number}? การลบเป็นแบบ soft-delete สามารถกู้คืนได้ภายหลัง`,
      async () => {
        try {
          await rentalService.deleteRental(rental.id);
          showAlert('สำเร็จ', 'ลบรายการเช่าเรียบร้อยแล้ว', 'success');
          navigate('/rentals/list');
        } catch (error: any) {
          // BUG-S2015-001 (KD3 S2015 NEG-RENT-006): stale tab ลบซ้ำ → backend 404
          // "ไม่พบรายการเช่า หรือรายการนี้ถูกลบไปแล้ว" แต่ผู้ใช้เห็นแค่ "ไม่สามารถลบได้"
          // เพราะอ่าน `.detail` ที่ KRP envelope ไม่มี — ใช้ helper เดียวกับ loadRentalData
          const msg = getApiErrorMessage(error, 'ไม่สามารถลบได้');
          showAlert('เกิดข้อผิดพลาด', msg, 'error');
          throw reportedError(msg);
        }
      },
      { isDanger: true, confirmText: 'ลบรายการ', cancelText: 'ยกเลิก' }
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="w-full">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!rental) {
    return (
      <Layout>
        <div className="w-full">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-secondary-900 mb-4">ไม่พบข้อมูลการเช่า</h1>
            <button
              onClick={() => navigate('/rentals/list')}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              กลับสู่รายการเช่า
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const actions = getRentalActions(rental, { hasPermission, canView, canEdit, canDelete });

  // ย้ายอุปกรณ์ข้ามโครงการ (device movements Phase 1) — ย้ายได้เฉพาะสถานะใช้งาน/รอยกเลิก
  const canMoveProject =
    (rental.status === 'ACTIVE' || rental.status === 'PENDING_CANCELLATION') &&
    hasPermission('device_movements.create');

  return (
    <Layout>
      <div className="w-full">
        {/* Breadcrumb — restore last list URL (?page=N + filters) when available */}
        <Breadcrumb items={[
          { label: 'รายการเช่า', path: (() => { try { return sessionStorage.getItem('krp:lastRentalListUrl') || '/rentals/list'; } catch { return '/rentals/list'; } })() },
          { label: 'รายละเอียด' }
        ]} />

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <RiEyeLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight">รายละเอียดการเช่า</h1>
              <p className="text-xs text-secondary-500 mt-0.5">ดูข้อมูลรายละเอียดการเช่าอุปกรณ์</p>
            </div>
          </div>
        </div>

        {/* Fix ก (ก-1): สัญญา "รอการยกเลิก" ที่รับของคืนครบแล้ว — เสนอปิดใบแจ้งยกเลิกทันที
            (ของกลับแล้วจริง แต่สัญญายังนับว่าถือของอยู่จนถึงวันที่แจ้งไว้ ⇒ picker หาไม่เจอทั้งที่
            ของอยู่ในมือ Support แล้ว) วางไว้บนสุดของเนื้อหา เต็มความกว้างหน้า — เห็นก่อนสิ่งอื่นทั้งหมด */}
        {finalizeStatus?.eligible && (
          <div className="mb-6 border rounded-lg bg-success-50 border-success-300">
            <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-6">
              <div className="flex items-start gap-3">
                <RiCheckboxCircleLine className="flex-shrink-0 w-6 h-6 mt-0.5 text-success-600" />
                <div>
                  <div className="font-semibold text-secondary-900">
                    {/* ยังไม่ส่งมอบ = ไม่มีอะไรให้รับคืน — หัวข้อต้องไม่อ้างว่า "รับคืนครบแล้ว" */}
                    {/* ของหาย ≠ รับคืน (มติ 29 ต.ค.) — หัวข้อต้องตรงกับกล่องยืนยัน FinalizeCancellationModal */}
                    {finalizeStatus.reason_code === 'ELIGIBLE_NOT_DELIVERED'
                      ? 'ยังไม่ได้ส่งมอบของ — ปิดใบแจ้งยกเลิกได้เลย'
                      : (finalizeStatus.lost?.length ?? 0) > 0
                      ? 'มีของหาย — ปิดใบแจ้งยกเลิกได้'
                      : 'รับคืนอุปกรณ์ครบแล้ว'}
                  </div>
                  <p className="mt-1 text-sm text-secondary-700">{finalizeStatus.message}</p>
                  {!finalizeStatus.can_finalize && finalizeStatus.blocked_reason && (
                    <p className="mt-1 text-sm text-warning-700">{finalizeStatus.blocked_reason}</p>
                  )}
                </div>
              </div>
              {finalizeStatus.can_finalize && (
                <button
                  type="button"
                  onClick={() => setFinalizeModalOpen(true)}
                  className="inline-flex items-center justify-center flex-shrink-0 gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-success-600 hover:bg-success-700 focus:outline-none focus:ring-2 focus:ring-success-500"
                >
                  ปิดใบแจ้งยกเลิก
                </button>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ข้อมูลทั่วไป */}
          <div className="lg:col-span-2 space-y-6">
            {/* ข้อมูลการเช่า */}
            <div className="bg-white rounded-lg shadow-card border border-secondary-200">
              <div className="px-4 sm:px-6 py-4 border-b border-secondary-200">
                <h2 className="text-base sm:text-lg font-medium text-secondary-900 flex items-center">
                  <RiFileListLine className="mr-2 h-5 w-5 text-secondary-400 flex-shrink-0" />
                  ข้อมูลการเช่า
                </h2>
              </div>
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">รหัสการเช่า</label>
                    <p className="mt-1 text-sm font-medium text-secondary-900">{rental.code || rental.rental_number}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">สถานะ</label>
                    <div className="mt-1">{getStatusBadge(rental.status)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">ประเภทการเช่า</label>
                    <p className="mt-1 text-sm text-secondary-900">{
                      rental.rental_type === 'RENT' ? 'เช่า' :
                      rental.rental_type === 'PURCHASE' ? 'ซื้อ' :
                      rental.rental_type === 'TRIAL' ? 'ทดลองใช้' :
                      rental.rental_type || 'เช่า'
                    }</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">วันที่เช่า</label>
                    <p className="mt-1 text-sm text-secondary-900 flex items-center">
                      <RiCalendarLine className="mr-1 h-4 w-4 text-secondary-400" />
                      {formatDate(rental.rental_date)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">วันที่เริ่มเก็บเงิน</label>
                    <p className="mt-1 text-sm text-secondary-900 flex items-center">
                      <RiMoneyDollarCircleLine className="mr-1 h-4 w-4 text-success-500" />
                      {formatDate(rental.billing_start_date || rental.rental_date)}
                      {/* แสดง Free Trial Badge ถ้า billing_start_date > rental_date
                          เทียบแบบ date-only ทั้งคู่ — API ส่ง rental_date เป็น datetime แต่ billing_start_date เป็น date
                          ถ้า new Date() ตรงๆ จะ parse คนละ timezone (UTC vs local) เกิด "ฟรี 1 วัน" ปลอมทั้งที่วันเดียวกัน */}
                      {rental.billing_start_date && rental.rental_date &&
                       String(rental.billing_start_date).slice(0, 10) > String(rental.rental_date).slice(0, 10) && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-info-100 text-info-700">
                          ทดลองใช้ฟรี {Math.round((Date.parse(String(rental.billing_start_date).slice(0, 10)) - Date.parse(String(rental.rental_date).slice(0, 10))) / 86400000)} วัน
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">วันที่คาดว่าจะครบสัญญา</label>
                    <p className="mt-1 text-sm text-secondary-900 flex items-center">
                      <RiCalendarLine className="mr-1 h-4 w-4 text-secondary-400" />
                      {rental.expected_return_date || rental.end_date
                        ? formatDate(rental.expected_return_date || rental.end_date)
                        : <span className="text-secondary-600">สัญญาเปิด (ไม่กำหนด)</span>
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500 flex items-center gap-1">
                      วันสิ้นสุดเก็บเงิน
                      <span
                        title="วันสุดท้ายที่ระบบจะเรียกเก็บเงิน ใช้คำนวณ proration ในใบแจ้งหนี้ · สัญญาที่จบแล้วแก้ได้ด้วยปุ่มดินสอ (สิทธิ์บัญชี)"
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-secondary-200 text-secondary-500 text-xs cursor-help"
                      >
                        ?
                      </span>
                    </label>
                    <p className="mt-1 text-sm text-secondary-900 flex items-center">
                      <RiMoneyDollarCircleLine className="mr-1 h-4 w-4 text-danger-400" />
                      {rental.billing_end_date
                        ? formatDate(rental.billing_end_date)
                        : <span className="text-secondary-500">— ยังเช่าอยู่ (Ongoing)</span>
                      }
                    </p>
                    {/* feedback amintra 29 ก.ย. 2026 RNT-20260716-0051: สัญญาที่จบแล้วแก้วันเก็บเงินได้ (สิทธิ์บัญชี)
                        onSaved ใช้ reloadRentalView (ไม่ใช่ loadRentalData เดี่ยว) เพื่อให้ "ประวัติการดำเนินการ" โหลดใหม่ด้วย */}
                    <BillingEndDateInlineEditor
                      rentalId={rental.id}
                      rentalStatus={rental.status}
                      billingEndDate={rental.billing_end_date ?? null}
                      actualReturnDate={rental.actual_return_date}
                      billingStartDate={rental.billing_start_date}
                      onSaved={reloadRentalView}
                    />
                  </div>
                  {rental.prepaid_until && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-500">ชำระล่วงหน้า</label>
                      <PrepaidUntilBadge prepaidUntil={rental.prepaid_until} prepaidNote={rental.prepaid_note} />
                    </div>
                  )}
                  {rental.actual_return_date && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-500">วันที่คืนจริง</label>
                      <p className="mt-1 text-sm text-secondary-900 flex items-center">
                        <RiCalendarLine className="mr-1 h-4 w-4 text-secondary-400" />
                        {formatDate(rental.actual_return_date)}
                      </p>
                    </div>
                  )}
                  {rental.delivery_date && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-500">วันที่ส่งมอบอุปกรณ์</label>
                      <p className="mt-1 text-sm text-secondary-900 flex items-center">
                        <RiCalendarLine className="mr-1 h-4 w-4 text-info-400" />
                        {formatDate(rental.delivery_date)}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">วันที่สร้าง</label>
                    <p className="mt-1 text-sm text-secondary-900 flex items-center">
                      <RiTimeLine className="mr-1 h-4 w-4 text-secondary-400" />
                      {formatDateTime(rental.created_at)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">วันที่แก้ไขล่าสุด</label>
                    <p className="mt-1 text-sm text-secondary-900 flex items-center">
                      <RiTimeLine className="mr-1 h-4 w-4 text-secondary-400" />
                      {formatDateTime(rental.updated_at || rental.created_at)}
                    </p>
                  </div>
                </div>
                {rental.notes && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-secondary-500 mb-2">หมายเหตุ</label>
                    <div className="bg-secondary-50 p-3 sm:p-4 rounded-md border border-secondary-200">
                      <p className="text-sm text-secondary-700 whitespace-pre-wrap [overflow-wrap:anywhere]">{rental.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ข้อมูลบริษัทและไซต์ */}
            <div className="bg-white rounded-lg shadow-card border border-secondary-200">
              <div className="px-4 sm:px-6 py-4 border-b border-secondary-200">
                <h2 className="text-base sm:text-lg font-medium text-secondary-900 flex items-center">
                  <RiBuilding2Line className="mr-2 h-5 w-5 text-secondary-400 flex-shrink-0" />
                  ข้อมูลบริษัทและโครงการ
                </h2>
              </div>
              <div className="p-4 sm:p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">บริษัท</label>
                    <p className="mt-1 text-sm font-medium text-secondary-900">
                      {rental.company_name || rental.company?.name || 'ไม่ระบุ'}
                    </p>
                    {/* G-12: ลิงก์ข้าม module ต้องมีสิทธิ์ปลายทาง (companies.view / sites.view) */}
                    <div className="flex flex-wrap gap-x-3">
                      {rental.company_id && hasPermission('companies.view') && (
                        <Link
                          to={companyDetailUrl(rental.company_id)}
                          className="text-xs text-primary-600 hover:text-primary-800"
                        >
                          ดูข้อมูลบริษัท
                        </Link>
                      )}
                      {/* pattern เดียวกับหน้ารายละเอียดซ่อมบำรุง: โครงการทั้งหมดของบริษัทนี้ (แท็บใหม่) — 2026-09-05 */}
                      {rental.company_id && hasPermission('sites.view') && (
                        <Link
                          to={siteListByCompanyUrl(rental.company_id)} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-primary-600 hover:text-primary-800"
                          data-testid="rental-view-company-sites-link"
                        >
                          ดูโครงการของบริษัท
                        </Link>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-500">โครงการ/ไซต์</label>
                    <p className="mt-1 text-sm font-medium text-secondary-900 flex items-center">
                      <RiMapPinLine className="mr-1 h-4 w-4 text-secondary-400" />
                      {rental.site_name || rental.site?.name || 'ไม่ระบุ'}
                    </p>
                    {rental.site?.address && (
                      <p className="mt-1 text-xs text-secondary-500">{rental.site.address}</p>
                    )}
                    {/* เปิดรายละเอียดโครงการนั้นทันที (แท็บใหม่) — pattern เดียวกับหน้ารายละเอียดซ่อมบำรุง 2026-09-05 */}
                    {(rental.site_id || rental.site?.id) && hasPermission('sites.view') && (
                      <Link
                        to={siteDetailUrl(rental.site_id || rental.site?.id, rental.company_id)} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-primary-600 hover:text-primary-800"
                        data-testid="rental-view-site-detail-link"
                      >
                        ดูรายละเอียดโครงการ
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* L1: ของที่ยังไม่ได้รับคืน — ต้องเห็นและปิดได้จากหน้านี้
                (หน้า "ติดตามการคืนอุปกรณ์" ถูกเปิดแค่ 2 ครั้งใน 90 วันบน prod และไม่เคยมีใคร
                 ปิดใบตามเก็บเลย ถ้าให้ปลดของได้ทางนั้นทางเดียว ของจะไม่กลับเข้าคลังอีกเลย) */}
            {outstandingReturns && outstandingReturns.outstanding_count > 0 && (
              <div className="bg-warning-50 rounded-lg border border-warning-300">
                <div className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start">
                    <RiAlertLine className="mr-2 h-5 w-5 text-warning-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-secondary-900">
                        อุปกรณ์ที่ยังไม่ได้รับคืน {outstandingReturns.outstanding_count} รายการ
                      </div>
                      <div className="text-sm text-secondary-600">
                        {outstandingReturns.items.map((it) => (
                          // 🔴 key ต้องเป็นราย "ชิ้น" ไม่ใช่ราย "รายการเช่า" — หลังกติกา
                          // "1 แถวใบตามเก็บ = ของ 1 ชิ้น" (9 ก.ย. 2026) rental_item เดียวคืน
                          // 2 บรรทัด (เครื่อง + ซิม) → key ซ้ำ React เตือน "two children with the
                          // same key" และ **อาจ omit บรรทัดใดบรรทัดหนึ่งทิ้ง** = การ์ดที่เป็นทาง
                          // หลักให้ของกลับเข้าคลังโชว์ไม่ครบ (เจอตอน E2E หน้าจอ 9 ก.ย. 2026)
                          <span
                            key={`${it.rental_item_id}-${it.device_id ?? 'nd'}-${it.sim_id ?? 'ns'}`}
                            className="inline-block mr-3"
                          >
                            {it.label}
                            {it.days_outstanding != null && it.days_outstanding > 0 && (
                              <span className="text-danger-600"> · ค้าง {it.days_outstanding} วัน</span>
                            )}
                          </span>
                        ))}
                      </div>
                      <div className="text-xs text-secondary-500 mt-1">
                        ระบบถือว่าของยังอยู่กับลูกค้า จึงยังไม่คืนเข้าคลังและนำไปให้เช่าใหม่ไม่ได้
                      </div>
                    </div>
                  </div>
                  {/* BUG-S2181-001: endpoint record-returns = rentals.return_equipment (fallback rentals.edit)
                      — เดิม gate แค่ canEdit ทำให้ role ที่มีสิทธิ์รับคืนอย่างเดียวมองไม่เห็นปุ่ม ทั้งที่ API ยอม */}
                  {(canEdit('rentals') || hasPermission('rentals.return_equipment')) && (
                    <LoadingButton
                      type="button"
                      loading={recordingReturns}
                      onClick={handleRecordReturns}
                      className="px-4 py-2 text-sm rounded-lg bg-warning-700 text-white hover:bg-warning-800 whitespace-nowrap"
                    >
                      รับคืนแล้ว - บันทึก
                    </LoadingButton>
                  )}
                </div>
              </div>
            )}

            {/* รายการอุปกรณ์ */}
            <div className="bg-white rounded-lg shadow-card border border-secondary-200">
              <div className="px-4 sm:px-6 py-4 border-b border-secondary-200">
                <h2 className="text-base sm:text-lg font-medium text-secondary-900 flex items-center">
                  <RiDeviceLine className="mr-2 h-5 w-5 text-secondary-400 flex-shrink-0" />
                  รายการอุปกรณ์ที่เช่า
                </h2>
              </div>

              {/* --- Mobile card layout (hidden on md+) --- */}
              <div className="md:hidden divide-y divide-secondary-100">
                {rental.rental_items && rental.rental_items.length > 0 ? (
                  <>
                    {rental.rental_items.map((item, index) => (
                      <div key={item.id} className="p-4 space-y-3">
                        {/* Card header: index + product name */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 min-w-0">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary-100 text-secondary-600 text-xs font-semibold flex items-center justify-center">
                              {index + 1}
                            </span>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-secondary-900 flex flex-wrap items-center gap-1">
                                {item.product_name || item.product?.name || 'ไม่ระบุสินค้า'}
                                {item.is_software_only && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700">
                                    SW
                                  </span>
                                )}
                              </div>
                              {item.is_software_only ? (
                                <p className="text-xs text-purple-600 mt-0.5">เช่า Software (ลูกค้าใช้เครื่องตัวเอง)</p>
                              ) : (
                                <p className="text-xs text-secondary-500 mt-0.5">
                                  {(item.device?.device_type?.name || 'อุปกรณ์') +
                                   ((item.device?.device_model?.name || item.device?.model) ? ` - ${item.device?.device_model?.name || item.device?.model}` : '')}
                                  {item.device && ` · รหัส: ${item.device.code}`}
                                </p>
                              )}
                            </div>
                          </div>
                          {/* G-27: gate เดียวกับปุ่มใน table layout — backend ใช้ device_replacements.create */}
                          {rental.status === 'ACTIVE' && canReplaceItem(item) && hasPermission('device_replacements.create') && (
                            <button
                              onClick={() => handleOpenReplacementModal(item)}
                              className="flex-shrink-0 inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-warning-700 bg-warning-100 rounded-md hover:bg-warning-200 transition-colors"
                            >
                              <RiExchangeLine className="w-3.5 h-3.5 mr-1" />
                              เปลี่ยน
                            </button>
                          )}
                        </div>

                        {/* Card body: key-value grid */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 bg-secondary-50 rounded-lg p-3 text-xs">
                          <div>
                            <span className="text-secondary-500">Serial Number</span>
                            <p className="font-medium text-secondary-900 mt-0.5 break-all">
                              {item.is_software_only ? '-' : (item.device?.serial_number || item.device_serial || '-')}
                            </p>
                            {getItemSimLabel(item) && (
                              <p className="text-secondary-600 mt-0.5 flex items-center gap-1">
                                <RiSimCardLine className="w-3 h-3 flex-shrink-0" />
                                <span className="font-mono">{getItemSimLabel(item)}</span>
                              </p>
                            )}
                          </div>
                          <div>
                            <span className="text-secondary-500">จำนวน</span>
                            <p className="font-medium text-secondary-900 mt-0.5">{item.quantity}</p>
                          </div>
                          <div>
                            <span className="text-secondary-500">ราคาเช่า/เดือน</span>
                            <p className="font-medium text-secondary-900 mt-0.5">฿{item.rental_rate?.toLocaleString() || '0'}</p>
                          </div>
                          <div>
                            <span className="text-secondary-500">ระยะเวลา</span>
                            <p className="font-medium text-secondary-900 mt-0.5">
                              {item.rental_period_days ? `${Math.round(item.rental_period_days / 30)} เดือน` : '-'}
                            </p>
                          </div>
                          <div>
                            <span className="text-secondary-500">รวม</span>
                            <p className="font-semibold text-secondary-900 mt-0.5">
                              ฿{(item.total_amount ?? ((item.rental_rate || 0) * (item.quantity || 1) * (item.rental_period_days || 0) / 30)).toLocaleString()}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <span className="text-secondary-500">มัดจำ</span>
                            <p className="font-medium text-secondary-900 mt-0.5">฿{item.deposit_amount?.toLocaleString() || '0'}</p>
                            <DepositStatusBadge item={item} className="mt-1" />
                            {renderDepositActions(item, false)}
                          </div>
                        </div>

                        {/* Remote Access (AnyDesk) — แก้ได้ตรงนี้ (ค่าเดียวกับหน้าอุปกรณ์/เคสซ่อม · 23 ก.ย. 2026) */}
                        {showAnydeskFor(item) && (
                          <div className="bg-primary-50 rounded-lg p-3 text-xs space-y-1">
                            <div className="flex items-center gap-1.5 font-semibold text-primary-700">
                              <RiRemoteControlLine className="w-3.5 h-3.5" />
                              Remote Access (AnyDesk)
                            </div>
                            <AnyDeskInlineEditor
                              anydeskId={item.anydesk_id}
                              notes={item.remote_access_notes}
                              onSave={(data) => saveItemRemoteAccess(item.id, data)}
                              showLabel={false}
                              compact
                              testId={`rental-item-anydesk-m-${item.id}`}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                    {/* Mobile total row */}
                    <div className="px-4 py-3 bg-secondary-50 flex items-center justify-between">
                      <span className="text-sm font-medium text-secondary-700">รวมทั้งสิ้น</span>
                      <span className="text-base font-bold text-secondary-900">฿{calculateTotalAmount().toLocaleString()}</span>
                    </div>
                  </>
                ) : rental.product_name ? (
                  <div className="p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary-100 text-secondary-600 text-xs font-semibold flex items-center justify-center">1</span>
                      <div>
                        <p className="text-sm font-semibold text-secondary-900">{rental.product_name}</p>
                        {rental.device_id && (
                          <p className="text-xs text-secondary-500 mt-0.5">
                            <RiDeviceLine className="inline h-3 w-3 mr-1" />
                            {rental.device_code || `Device #${rental.device_id}`}
                          </p>
                        )}
                        {rental.sim_number && (
                          <p className="text-xs text-secondary-500 mt-0.5">
                            <RiSimCardLine className="inline h-3 w-3 mr-1" />
                            {rental.sim_number}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 bg-secondary-50 rounded-lg p-3 text-xs">
                      <div>
                        <span className="text-secondary-500">ราคาเช่า/เดือน</span>
                        <p className="font-medium text-secondary-900 mt-0.5">฿{rental.monthly_fee?.toLocaleString() || '0'}</p>
                      </div>
                      <div>
                        <span className="text-secondary-500">รวมทั้งสิ้น</span>
                        <p className="font-bold text-secondary-900 mt-0.5">฿{rental.total_amount?.toLocaleString() || '0'}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center text-sm text-secondary-500">
                    ไม่มีรายการอุปกรณ์
                  </div>
                )}
              </div>

              {/* --- Desktop table layout (hidden on mobile) --- */}
              <div className="hidden md:block overflow-x-auto" tabIndex={0} role="region" aria-label="ตารางรายการอุปกรณ์ในการเช่า">
                {/* E2E v5 FAIL-B14: คอลัมน์มัดจำ (w-44) ทำให้คอลัมน์สินค้าเหลือ ~50px ที่ 1366 → รหัสเครื่องแตก 4 บรรทัด
                    แถวสูง 176px — ขยาย min-width ตาราง + กันคอลัมน์สินค้าไม่ให้ต่ำกว่า 220px (เลื่อนแนวนอนใน wrapper แทน) */}
                <table className="min-w-[1100px] w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50 border-b-2 border-secondary-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600 w-12">
                        ลำดับ
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600 min-w-[220px]">
                        สินค้า/อุปกรณ์
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600 w-36">
                        Serial Number
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-secondary-600 w-16">
                        จำนวน
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-secondary-600 w-28">
                        ราคาเช่า/เดือน
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-secondary-600 w-24">
                        ระยะเวลา
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-secondary-600 w-28">
                        รวม
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-secondary-600 w-44">
                        มัดจำ
                      </th>
                      {rental.status === 'ACTIVE' && (
                        <th className="px-4 py-3 text-center text-xs font-semibold text-secondary-600 w-24">
                          การดำเนินการ
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {rental.rental_items && rental.rental_items.length > 0 ? (
                      rental.rental_items.map((item, index) => (
                        <tr key={item.id} className="hover:bg-secondary-50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-500 text-center">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <div className="text-sm font-medium text-secondary-900 flex items-center gap-2">
                                {item.product_name || item.product?.name || 'ไม่ระบุสินค้า'}
                                {item.is_software_only && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700">
                                    SW
                                  </span>
                                )}
                              </div>
                              {item.is_software_only ? (
                                <div className="text-xs text-purple-600 font-medium mt-0.5">
                                  เช่า Software อย่างเดียว (ลูกค้าใช้เครื่องตัวเอง)
                                </div>
                              ) : (
                                <>
                                  <div className="text-xs text-secondary-500 mt-0.5">
                                    {(item.device?.device_type?.name || 'อุปกรณ์') +
                                     ((item.device?.device_model?.name || item.device?.model) ? ` - ${item.device?.device_model?.name || item.device?.model}` : '')}
                                  </div>
                                  {item.device && (
                                    <div className="text-xs text-secondary-500">
                                      รหัส: {item.device.code}
                                    </div>
                                  )}
                                </>
                              )}
                              {/* Remote Access (AnyDesk) — แก้ได้ตรงนี้ (ค่าเดียวกับหน้าอุปกรณ์/เคสซ่อม · 23 ก.ย. 2026) */}
                              {showAnydeskFor(item) && (
                                <AnyDeskInlineEditor
                                  anydeskId={item.anydesk_id}
                                  notes={item.remote_access_notes}
                                  onSave={(data) => saveItemRemoteAccess(item.id, data)}
                                  compact
                                  className="mt-1"
                                  testId={`rental-item-anydesk-${item.id}`}
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-900">
                            {item.is_software_only ? '-' : (item.device?.serial_number || item.device_serial || '-')}
                            {getItemSimLabel(item) && (
                              <div className="text-xs text-secondary-600 mt-0.5 flex items-center gap-1" title="SIM ในรายการนี้">
                                <RiSimCardLine className="w-3 h-3 flex-shrink-0" />
                                <span className="font-mono">{getItemSimLabel(item)}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-900 text-center">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-900 text-right">
                            ฿{item.rental_rate?.toLocaleString() || '0'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-900 text-center">
                            {item.rental_period_days ? `${Math.round(item.rental_period_days / 30)} เดือน` : '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-secondary-900 text-right">
                            ฿{(item.total_amount ?? ((item.rental_rate || 0) * (item.quantity || 1) * (item.rental_period_days || 0) / 30)).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center align-top">
                            <div className="text-sm text-secondary-900">
                              ฿{item.deposit_amount?.toLocaleString() || '0'}
                            </div>
                            <DepositStatusBadge item={item} align="center" className="mt-1" />
                            {renderDepositActions(item, true)}
                          </td>
                          {rental.status === 'ACTIVE' && (
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              {/* G-27: gate ให้ตรง backend POST /device-replacements (device_replacements.create)
                                  — เดิมแสดงทุกคนที่เปิดหน้าได้ ผู้ใช้ไม่มีสิทธิ์กรอกฟอร์มเสร็จค่อยเจอ 403 (report Mint/Accounting 2026-08-26) */}
                              {canReplaceItem(item) && hasPermission('device_replacements.create') && (
                                <button
                                  onClick={() => handleOpenReplacementModal(item)}
                                  className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 text-xs font-medium text-warning-700 bg-warning-100 rounded-md hover:bg-warning-200 transition-colors"
                                  title="เปลี่ยนอุปกรณ์"
                                >
                                  <RiExchangeLine className="w-4 h-4 mr-1" />
                                  เปลี่ยน
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    ) : rental.product_name ? (
                      // Fallback for legacy single item display
                      <tr>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-500 text-center">1</td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-sm font-medium text-secondary-900">
                              {rental.product_name}
                            </div>
                            {rental.device_id && (
                              <div className="text-xs text-secondary-500 mt-0.5">
                                <RiDeviceLine className="inline h-3 w-3 mr-1" />
                                {rental.device_code || `Device #${rental.device_id}`}
                              </div>
                            )}
                            {rental.sim_number && (
                              <div className="text-xs text-secondary-500 mt-0.5">
                                <RiSimCardLine className="inline h-3 w-3 mr-1" />
                                {rental.sim_number}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-900">
                          {rental.device_code || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-900 text-center">1</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-900 text-right">
                          ฿{rental.monthly_fee?.toLocaleString() || '0'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-secondary-900 text-center">
                          {rental.end_date || rental.expected_return_date
                            ? `${Math.ceil((new Date(rental.end_date || rental.expected_return_date).getTime() - new Date(rental.rental_date).getTime()) / (1000 * 60 * 60 * 24))} วัน`
                            : 'สัญญาเปิด'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-secondary-900 text-right">
                          ฿{rental.total_amount?.toLocaleString() || '0'}
                        </td>
                        <td className="px-4 py-3" />
                        {rental.status === 'ACTIVE' && <td className="px-4 py-3" />}
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan={rental.status === 'ACTIVE' ? 9 : 8} className="px-4 py-8 text-center text-sm text-secondary-500">
                          ไม่มีรายการอุปกรณ์
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-secondary-50 border-t-2 border-secondary-200">
                    <tr>
                      {/*
                        col1=ลำดับ col2=สินค้า col3=S/N col4=จำนวน col5=ราคา/เดือน
                        col6=ระยะเวลา col7=รวม col8=มัดจำ [col9=การดำเนินการ ถ้า ACTIVE]
                        → span cols 1-6, แสดง label ใน col7, มูลค่าใน col8, ว่าง col9
                      */}
                      <td colSpan={6} className="px-4 py-3" />
                      <td className="px-4 py-3 text-right text-sm font-medium text-secondary-900 whitespace-nowrap">
                        รวมทั้งสิ้น:
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-secondary-900 text-right">
                        ฿{calculateTotalAmount().toLocaleString()}
                      </td>
                      {rental.status === 'ACTIVE' && <td />}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Activity Log */}
            <div className="bg-white rounded-lg shadow-card border border-secondary-200">
              <div className="px-4 sm:px-6 py-4 border-b border-secondary-200">
                <h2 className="text-base sm:text-lg font-medium text-secondary-900 flex items-center">
                  <RiHistoryLine className="mr-2 h-5 w-5 text-secondary-400 flex-shrink-0" />
                  ประวัติการดำเนินการ
                </h2>
              </div>
              <div className="p-4 sm:p-6">
                {loadingLogs ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  </div>
                ) : activityLogs.length > 0 ? (
                  <div className="flow-root">
                    <ul className="-mb-8">
                      {activityLogs.map((log, logIdx) => (
                        <li key={log.id}>
                          <div className="relative pb-8">
                            {logIdx !== activityLogs.length - 1 ? (
                              <span
                                className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-secondary-200"
                                aria-hidden="true"
                              />
                            ) : null}
                            <div className="relative flex space-x-3">
                              <div className="flex-shrink-0">
                                <span className="h-8 w-8 rounded-full bg-secondary-100 flex items-center justify-center ring-8 ring-white">
                                  <RiTimeLine className="h-4 w-4 text-secondary-500" />
                                </span>
                              </div>
                              <div className="flex min-w-0 flex-1 justify-between gap-2 pt-1.5">
                                <div className="min-w-0">
                                  <p className="text-sm text-secondary-900">
                                    {getActionLabel(log.action)}
                                    {log.description && (
                                      <span className="text-secondary-500"> - {log.description}</span>
                                    )}
                                  </p>
                                  <p className="mt-0.5 text-xs text-secondary-500">
                                    โดย {log.user_name || log.user_email || 'ระบบ'}
                                  </p>
                                </div>
                                <div className="flex-shrink-0 text-right text-xs text-secondary-500">
                                  {formatDateTime(log.created_at)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {/* P-07 (E2E v5 deposit/rental): ปุ่มโหลดหน้าถัดไป + ตัวนับ — แสดงเมื่อยังมีรายการเก่ากว่าที่ยังไม่ได้ดึง
                        (ul มี -mb-8 หักล้าง pb-8 ของ li สุดท้าย → mt-4 ให้เว้นจากรายการสุดท้าย 1rem) */}
                    {activityLogs.length < activityTotal && (
                      // relative z-10: li สุดท้าย (pb-8 ใต้ ul -mb-8) ทับพื้นที่ปุ่ม 16px บน ทำให้คลิกไม่ติด (E2E v5 B-p)
                      <div className="relative z-10 mt-4 flex flex-col items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => loadActivityLogs(activityPage + 1)}
                          disabled={loadingMoreLogs}
                          className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md text-secondary-700 bg-secondary-100 hover:bg-secondary-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {loadingMoreLogs
                            ? 'กำลังโหลด...'
                            : `ดูประวัติเพิ่ม (เหลืออีก ${(activityTotal - activityLogs.length).toLocaleString()} รายการ)`}
                        </button>
                        <p className="text-xs text-secondary-500">
                          แสดง {activityLogs.length.toLocaleString()} จาก {activityTotal.toLocaleString()} รายการ
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-secondary-500 text-center py-4">ไม่มีประวัติการดำเนินการ</p>
                )}
              </div>
            </div>

            {/* ส่วนหมายเหตุ */}
            <RentalNotesSection rentalId={rental.id} />

            {/* ประวัติการซ่อมบำรุง */}
            <MaintenanceHistorySection rentalId={rental.id} />

            {/* ประวัติการเปลี่ยนอุปกรณ์ — ต้องมี device_replacements.view (ตรง backend GET)
                ไม่งั้น component ยิง API แล้วโดน 403 เงียบๆ (G-27) */}
            {hasPermission('device_replacements.view') && (
              <DeviceReplacementHistory rentalId={rental.id} />
            )}

            {/* ประวัติการย้ายโครงการ — ต้องมี device_movements.view (ตรง backend GET)
                ไม่งั้น component ยิง API แล้วโดน 403 เงียบๆ (pattern G-27) */}
            {hasPermission('device_movements.view') && (
              <DeviceMovementHistorySection rentalId={rental.id} />
            )}

            {/* ใบงานเตรียมเครื่องต้นทางของสัญญานี้ (PO 2026-09-08)
                การ์ดซ่อนตัวเองเมื่อไม่มีสิทธิ์ดู หรือสัญญานี้ไม่ได้มาจากใบงาน */}
            <RentalPreparationJobSection rentalId={rental.id} />

            {/* ใบเสนอราคาต้นทางของสัญญานี้ (มติ PO 29 ก.ย. 2026 เฟส 4) — การ์ดซ่อนตัวเองเมื่อไม่มีสิทธิ์
                quotations.view (ไม่ยิง API) หรือสัญญานี้ไม่ได้มาจากใบเสนอราคา */}
            <RentalQuotationSection rentalId={rental.id} />

            {/* ใบแจ้งยกเลิก / ใบตามเก็บของสัญญานี้ → หน้าเต็มของแต่ละใบ (28 ต.ค. 2026) — ซ่อนตัวเองเมื่อไม่มีใบ/ไม่มีสิทธิ์ */}
            <RentalReturnAndCancellationSection rentalId={rental.id} />

            {/* ใบเบิกอุปกรณ์ที่ผูกกับสัญญานี้ (แทน "ขออุปกรณ์เสริม" ที่ถูกยุบ — PO 2026-09-07) */}
            <RentalStockIssueSection
              rentalId={rental.id}
              rentalNumber={rental.code || rental.rental_number}
            />

            {/* ประวัติการขออุปกรณ์เสริม (ข้อมูลเก่าอย่างเดียว — โมดูลถูกยุบแล้ว
                การ์ดนี้ซ่อนตัวเองเมื่อไม่มีประวัติ) */}
            <AccessoryRequestHistorySection rentalId={rental.id} />
          </div>

          {/* แผงด้านข้าง */}
          <div className="space-y-4 sm:space-y-6">
            {/* ข้อมูลสัญญา */}
            {rental.contract_id && (
              <div className="bg-white rounded-lg shadow-card border border-secondary-200">
                <div className="px-4 sm:px-6 py-4 border-b border-secondary-200">
                  <h3 className="text-base font-medium text-secondary-900 flex items-center">
                    <RiFileTextLine className="mr-2 h-5 w-5 text-secondary-400 flex-shrink-0" />
                    ข้อมูลสัญญา
                  </h3>
                </div>
                <div className="p-4 sm:p-6 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-secondary-500 uppercase tracking-wide">เลขที่สัญญา</label>
                    {/* G-12: ลิงก์ข้าม module ต้องมีสิทธิ์ปลายทาง (contracts.view) — ไม่มีสิทธิ์ = แสดงเลขที่แบบ text */}
                    {hasPermission('contracts.view') ? (
                      <Link
                        to={`/contracts/view/${rental.contract_id}`} target="_blank" rel="noopener noreferrer"
                        className="mt-1 text-sm font-medium text-primary-600 hover:text-primary-800 flex items-center gap-1"
                      >
                        <RiFileTextLine className="h-4 w-4 flex-shrink-0" />
                        {rental.contract?.contract_number || rental.contract_number || `สัญญา #${rental.contract_id}`}
                      </Link>
                    ) : (
                      <p className="mt-1 text-sm font-medium text-secondary-900 flex items-center gap-1">
                        <RiFileTextLine className="h-4 w-4 flex-shrink-0 text-secondary-400" />
                        {rental.contract?.contract_number || rental.contract_number || `สัญญา #${rental.contract_id}`}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-secondary-500 uppercase tracking-wide">สถานะสัญญา</label>
                    {/* BUG-S2172-001: สถานะสัญญา (DRAFT/ACTIVE/EXPIRED/TERMINATED) ไม่ใช่สถานะการเช่า — เดิมส่งเข้า
                        getStatusBadge ของ rental ที่ไม่รู้จัก EXPIRED/TERMINATED จึงขึ้นค่าดิบภาษาอังกฤษ */}
                    <div className="mt-1">
                      {rental.contract?.status
                        ? <ContractStatusBadge status={String(rental.contract.status).toUpperCase() as any} size="sm" />
                        : getStatusBadge(rental.status)}
                    </div>
                  </div>
                  {hasPermission('contracts.view') && (
                    <div className="pt-1">
                      <Link
                        to={`/contracts/view/${rental.contract_id}`} target="_blank" rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center px-4 min-h-[44px] sm:min-h-[38px] py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
                      >
                        <RiFileTextLine className="mr-2 h-4 w-4" />
                        ดูสัญญา
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ประวัติสัญญา */}
            {rental.contract_id && (
              <ContractHistorySection contractId={rental.contract_id} />
            )}

            {/* การดำเนินการ */}
            <div className="bg-white rounded-lg shadow-card border border-secondary-200">
              <div className="px-4 sm:px-6 py-4 border-b border-secondary-200">
                <h3 className="text-base font-medium text-secondary-900 flex items-center">
                  <RiShieldCheckLine className="mr-2 h-5 w-5 text-secondary-400 flex-shrink-0" />
                  การดำเนินการ
                </h3>
              </div>
              <div className="p-4 sm:p-6 space-y-3">
                {/* ===== approve — standalone primary at top ===== */}
                {actions.approve && (
                  <button
                    onClick={handleApprove}
                    className="w-full inline-flex flex-col items-center justify-center px-4 py-2.5 bg-success-700 text-white rounded-md hover:bg-success-800 transition-colors"
                  >
                    <span className="inline-flex items-center text-sm font-medium"><RiCheckLine className="mr-2 h-4 w-4" />อนุมัติ → เริ่มเช่า</span>
                    <span className="text-xs font-normal text-success-100 mt-0.5">{RENTAL_ACTION_HINTS.approve}</span>
                  </button>
                )}

                {/* ===== กลุ่ม: จัดการการเช่า (แก้ไข / ต่ออายุ / ย้ายโครงการ) ===== */}
                {(actions.edit || actions.extend || canMoveProject) && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">จัดการการเช่า</p>
                    {actions.edit && (
                      <button
                        onClick={() => navigate(`/rentals/edit/${rental.id}`)}
                        className="w-full inline-flex flex-col items-center justify-center px-4 py-2.5 bg-warning-700 text-white rounded-md hover:bg-warning-800 transition-colors"
                      >
                        <span className="inline-flex items-center text-sm font-medium"><RiEditLine className="mr-2 h-4 w-4" />แก้ไขรายการเช่า</span>
                        <span className="text-xs font-normal text-warning-100 mt-0.5">ปรับข้อมูลการเช่า</span>
                      </button>
                    )}
                    {actions.extend && (
                      <button
                        onClick={() => setShowExtensionModal(true)}
                        className="w-full inline-flex flex-col items-center justify-center px-4 py-2.5 bg-info-600 text-white rounded-md hover:bg-info-700 transition-colors"
                      >
                        <span className="inline-flex items-center text-sm font-medium"><RiCalendarAddLine className="mr-2 h-4 w-4" />ต่ออายุการเช่า</span>
                        <span className="text-xs font-normal text-white mt-0.5">ขยายระยะเวลาการเช่า</span>
                      </button>
                    )}
                    {canMoveProject && (
                      <button
                        onClick={() => setShowMovementModal(true)}
                        className="w-full inline-flex flex-col items-center justify-center px-4 py-2.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                      >
                        <span className="inline-flex items-center text-sm font-medium"><RiTruckLine className="mr-2 h-4 w-4" />ย้ายโครงการ</span>
                        <span className="text-xs font-normal text-primary-100 mt-0.5">ย้ายอุปกรณ์ทั้งหมดหรือบางรายการไปโครงการอื่นในบริษัทเดียวกัน</span>
                      </button>
                    )}
                  </div>
                )}

                {/* ===== กลุ่ม: จบการเช่า (รับคืน / ยกเลิก / ลบ) ===== */}
                {(actions.receiveReturn || actions.cancel || actions.delete) && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">จบการเช่า</p>
                    {actions.receiveReturn && (
                      <button
                        onClick={() => setShowReturnModal(true)}
                        className="w-full inline-flex flex-col items-center justify-center px-4 py-2.5 bg-success-700 text-white rounded-md hover:bg-success-800 transition-colors"
                      >
                        <span className="inline-flex items-center text-sm font-medium"><RiArrowGoBackLine className="mr-2 h-4 w-4" />ของกลับครบแล้ว–ปิดการเช่า</span>
                        <span className="text-xs font-normal text-success-100 mt-0.5">{RENTAL_ACTION_HINTS.receiveReturn}</span>
                      </button>
                    )}
                    {actions.cancel && actions.cancelMode !== 'continue' && (
                      <button
                        onClick={() => handleCancelAction(actions.cancelMode)}
                        className="w-full inline-flex flex-col items-center justify-center px-4 py-2.5 border border-danger-300 text-danger-700 bg-white rounded-md hover:bg-danger-50 transition-colors"
                      >
                        <span className="inline-flex items-center text-sm font-medium"><RiCloseCircleLine className="mr-2 h-4 w-4" />{actions.cancelMode === 'void' ? 'ยกเลิกร่าง' : 'ลูกค้าแจ้งเลิกเช่า'}</span>
                        <span className="text-xs font-normal text-secondary-600 mt-0.5">{actions.cancelMode === 'void' ? RENTAL_ACTION_HINTS.cancelVoid : RENTAL_ACTION_HINTS.cancelFlow}</span>
                      </button>
                    )}
                    {actions.cancel && actions.cancelMode === 'continue' && (
                      <div className="space-y-2">
                        <p className="text-xs text-secondary-500 text-center">รายการนี้รอยกเลิก — เลือกได้ 2 ทาง:</p>
                        <button
                          onClick={() => navigate(`/rentals/cancellation-tracking?rentalId=${rental.id}&intent=retain`)}
                          className="w-full inline-flex flex-col items-center justify-center px-4 py-2.5 bg-success-600 text-white rounded-md hover:bg-success-700 transition-colors"
                        >
                          <span className="inline-flex items-center text-sm font-medium"><RiCheckLine className="mr-2 h-4 w-4" />เก็บลูกค้าไว้ — กลับมาเช่าต่อ</span>
                          <span className="text-xs font-normal text-success-100 mt-0.5">ยกเลิกการแจ้งเลิก ลูกค้ายังเช่าต่อ</span>
                        </button>
                        <button
                          onClick={() => navigate(`/rentals/cancellation-tracking?rentalId=${rental.id}&intent=cancel`)}
                          className="w-full inline-flex flex-col items-center justify-center px-4 py-2.5 border border-danger-300 text-danger-700 bg-white rounded-md hover:bg-danger-50 transition-colors"
                        >
                          <span className="inline-flex items-center text-sm font-medium"><RiCloseCircleLine className="mr-2 h-4 w-4" />ยืนยันยกเลิก / ติดตามการคืน</span>
                          <span className="text-xs font-normal text-secondary-600 mt-0.5">ดำเนินการยกเลิกและติดตามอุปกรณ์</span>
                        </button>
                      </div>
                    )}
                    {actions.delete && (
                      <button
                        onClick={handleDeleteRental}
                        className="w-full inline-flex flex-col items-center justify-center px-4 py-2.5 bg-danger-600 text-white rounded-md hover:bg-danger-700 transition-colors"
                      >
                        <span className="inline-flex items-center text-sm font-medium"><RiDeleteBinLine className="mr-2 h-4 w-4" />ลบรายการเช่า</span>
                        <span className="text-xs font-normal text-danger-100 mt-0.5">ลบรายการเช่านี้ (กู้คืนได้ภายหลัง)</span>
                      </button>
                    )}
                  </div>
                )}

                {/* ===== requestReturn — demoted: secondary text link ===== */}
                {actions.requestReturn && (
                  <button
                    onClick={() => navigate(`/rentals/equipment-returns?rentalId=${rental.id}`)}
                    className="w-full text-sm text-primary-600 hover:text-primary-800 hover:underline text-center py-1 transition-colors"
                  >
                    เปิดใบตามเก็บอุปกรณ์ — ของยังไม่กลับ? ติดตามการคืน →
                  </button>
                )}

                {/* ไม่มี action เลย */}
                {!actions.approve && !actions.receiveReturn && !actions.requestReturn && !actions.edit && !actions.extend && !actions.cancel && !actions.delete && !canMoveProject && (
                  <p className="text-sm text-secondary-400 text-center py-2">ไม่มีการดำเนินการสำหรับสถานะนี้</p>
                )}
              </div>
            </div>

            {/* สถิติและข้อมูลการเงิน */}
            <div className="bg-white rounded-lg shadow-card border border-secondary-200">
              <div className="px-4 sm:px-6 py-4 border-b border-secondary-200">
                <h3 className="text-base font-medium text-secondary-900 flex items-center">
                  <RiMoneyDollarCircleLine className="mr-2 h-5 w-5 text-secondary-400 flex-shrink-0" />
                  ข้อมูลการเงิน
                </h3>
              </div>
              <div className="p-4 sm:p-6">
                <div className="bg-secondary-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-secondary-600">จำนวนรายการ:</span>
                    <span className="text-sm font-medium text-secondary-900">
                      {rental.rental_items?.length || (rental.product_name ? 1 : 0)} รายการ
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-secondary-600">ค่าเช่ารายเดือน:</span>
                    <span className="text-sm font-medium text-secondary-900">
                      ฿{rental.monthly_fee?.toLocaleString() || '0'}
                    </span>
                  </div>
                  {/* Deposit v2: สรุปมัดจำจาก /deposit-summary (รวม/รอคืน/คงไว้/คืนแล้ว) — ยังไม่โหลด/ล้มเหลว → แสดงยอดรวมเดิม */}
                  {depositSummary && depositSummary.total > 0 ? (
                    <div className="space-y-1" data-testid="deposit-summary">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-secondary-600">มัดจำรวม:</span>
                        <span className="text-sm font-medium text-secondary-900">
                          ฿{depositSummary.total.toLocaleString()}
                        </span>
                      </div>
                      {depositSummary.paid.count > 0 && (
                        <div className="flex justify-between items-center pl-3">
                          <span className="text-xs text-success-700">ชำระแล้ว ({depositSummary.paid.count})</span>
                          <span className="text-xs font-medium text-success-700">฿{depositSummary.paid.amount.toLocaleString()}</span>
                        </div>
                      )}
                      {depositSummary.pending_refund.count > 0 && (
                        <div className="flex justify-between items-center pl-3">
                          <span className="text-xs text-orange-700">รอคืน ({depositSummary.pending_refund.count})</span>
                          <span className="text-xs font-medium text-orange-700">฿{depositSummary.pending_refund.amount.toLocaleString()}</span>
                        </div>
                      )}
                      {depositSummary.held.count > 0 && (
                        <div className="flex justify-between items-center pl-3">
                          <span className="text-xs text-purple-700">คงไว้ ({depositSummary.held.count})</span>
                          <span className="text-xs font-medium text-purple-700">฿{depositSummary.held.amount.toLocaleString()}</span>
                        </div>
                      )}
                      {depositSummary.returned.count > 0 && (
                        <div className="flex justify-between items-center pl-3">
                          <span className="text-xs text-info-700">คืนแล้ว ({depositSummary.returned.count})</span>
                          <span className="text-xs font-medium text-info-700">฿{depositSummary.returned.amount.toLocaleString()}</span>
                        </div>
                      )}
                      {depositSummary.overdue_count > 0 && (
                        <p className="text-xs text-danger-600 font-medium pl-3">
                          เกินกำหนดคืนมัดจำ {depositSummary.overdue_count} รายการ
                        </p>
                      )}
                    </div>
                  ) : rental.deposit_amount > 0 ? (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-secondary-600">มัดจำรวม:</span>
                      <span className="text-sm font-medium text-secondary-900">
                        ฿{rental.deposit_amount?.toLocaleString()}
                      </span>
                    </div>
                  ) : null}
                  <div className="pt-2 mt-1 border-t border-secondary-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-secondary-700">มูลค่ารวม:</span>
                      <span className="text-lg font-bold text-secondary-900">
                        ฿{calculateTotalAmount().toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* FB-2026-0018: มัดจำชำระแล้วตามใบแจ้งหนี้จากใบเสนอราคา + ปุ่ม "บันทึกรับมัดจำจากใบนี้" (หาไม่เจอ = ไม่แสดง) */}
                <div className="mt-3">
                  <DepositInvoiceBanner rentalId={rental.id} refreshTrigger={depositSummary} onRecorded={reloadRentalView} />
                </div>

                {/* Invoice/Payment buttons removed - will be replaced with Company-level system */}
                {/*
                <div className="space-y-2 mt-4">
                  <button
                    onClick={() => setShowInvoiceListModal(true)}
                    className="w-full inline-flex items-center justify-center px-4 min-h-[44px] sm:min-h-[38px] py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                  >
                    <RiFileList3Line className="mr-2 h-4 w-4" />
                    จัดการใบแจ้งหนี้
                  </button>
                  <button
                    onClick={() => setShowPaymentListModal(true)}
                    className="w-full inline-flex items-center justify-center px-4 min-h-[44px] sm:min-h-[38px] py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                  >
                    <RiWallet3Line className="mr-2 h-4 w-4" />
                    จัดการการชำระเงิน
                  </button>
                </div>
                */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Return Equipment Modal */}
      {rental && (
        <ReturnEquipmentModal
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
          onConfirm={handleReturnEquipment}
          rental={rental}
        />
      )}

      {/* Device Replacement Modal */}
      <DeviceReplacementModal
        isOpen={showReplacementModal}
        onClose={() => {
          setShowReplacementModal(false);
          setSelectedRentalItem(null);
        }}
        rentalItem={selectedRentalItem}
        onSuccess={handleReplacementSuccess}
      />

      {/* Deposit v2: บันทึกคืน / คงมัดจำ (ต่อรายการ) */}
      {rental && (
        <DepositReturnModal
          isOpen={!!depositReturnItem}
          onClose={() => {
            setDepositReturnItem(null);
            // สถานะ hold อาจเปลี่ยนจากที่อื่นระหว่างเปิด modal (409 DEPOSIT_ON_HOLD stale) — โหลดใหม่ให้เปิดรอบหน้าเห็น banner/checkbox ปลด
            reloadRentalView();
          }}
          rentalId={rental.id}
          rentalStatus={rental.status}
          item={depositReturnItem}
          onSuccess={reloadRentalView}
        />
      )}

      {/* Deposit v2: บันทึกรับมัดจำ (PENDING → PAID) */}
      {rental && (
        <DepositMarkPaidModal
          isOpen={!!depositMarkPaidItem}
          onClose={() => setDepositMarkPaidItem(null)}
          rentalId={rental.id}
          item={depositMarkPaidItem}
          onSuccess={reloadRentalView}
        />
      )}

      {/* Rental Extension Modal */}
      <RentalExtensionModal
        isOpen={showExtensionModal}
        onClose={() => setShowExtensionModal(false)}
        rentalId={rental?.id || 0}
        rentalCode={rental?.code || rental?.rental_number || ''}
        currentEndDate={rental?.expected_return_date || rental?.end_date || ''}
        currentRentalFee={rental?.monthly_fee}
        onSuccess={() => { setShowExtensionModal(false); reloadRentalView(); }}
      />

      {/* Device Movement Modal (ย้ายอุปกรณ์ข้ามโครงการ) */}
      <DeviceMovementModal
        isOpen={showMovementModal}
        onClose={() => setShowMovementModal(false)}
        rentalId={rental?.id || null}
        onSuccess={() => { setShowMovementModal(false); reloadRentalView(); }}
      />

      {/* Rental Cancellation Flow Modal */}
      <RentalCancellationFlowModal
        isOpen={showCancellationFlowModal}
        onClose={() => setShowCancellationFlowModal(false)}
        rental={rental as any}
        onSelectNotification={() => {
          setShowCancellationFlowModal(false);
          if (rental) navigate(`/rentals/cancellation-tracking?rentalId=${rental.id}`);
        }}
        onSelectImmediate={() => {
          setShowCancellationFlowModal(false);
          if (!rental) return;
          // L1: ยกเลิกทันทีไม่ปล่อยของเองแล้ว — ต้องถามก่อนว่าของกลับมาหรือยัง
          // (เดิมเป็น confirm ธรรมดาที่บอกว่า "อุปกรณ์/SIM จะถูกปล่อยคืน" ซึ่งไม่จริงอีกต่อไป)
          setShowCancelGoodsModal(true);
        }}
      />

      {/* L1: ถาม "ของกลับแล้วหรือยัง" ตอนยกเลิก — ตอบตรงนี้ ไม่ต้องไปหน้าติดตามการคืน */}
      {rental && (
        <CancelGoodsCheckModal
          isOpen={showCancelGoodsModal}
          onClose={() => setShowCancelGoodsModal(false)}
          rentalNumber={rental.code || rental.rental_number}
          outstandingCount={outstandingReturns?.outstanding_count || 0}
          deliveryDate={rental.delivery_date}
          canEditRental={hasPermission('rentals.edit')}
          onConfirm={async ({ delivery_date, ...answer }) => {
            try {
              // ผู้ใช้เพิ่งยืนยันว่าส่งของไปแล้วเมื่อไร — ต้องบันทึก **ก่อน** ยกเลิก เพราะ
              // `delivery_date` คือเกณฑ์เดียวที่ backend ใช้ตัดสินว่าจะออกใบตามเก็บหรือ
              // ปล่อยของกลับคลัง (BUG-UI-08) · ส่งเฉพาะคีย์นี้คีย์เดียว — ไม่ส่ง rental_items
              // จึงไม่แตะรายการอุปกรณ์เลย (crud/rental ข้าม diff เมื่อไม่มี items ใน payload)
              if (delivery_date) {
                await rentalService.updateRental(rental.id, { delivery_date });
              }
              await rentalService.updateRentalStatus(
                rental.id, 'CANCELLED', undefined, undefined, answer
              );
              showAlert('สำเร็จ', 'ยกเลิกการเช่าเรียบร้อยแล้ว', 'success');
              reloadRentalView();
            } catch (error: any) {
              // BUG-S2015-001: ใช้ getApiErrorMessage อ่าน KRP envelope (เดิม `.detail` → generic เสมอ)
              const msg = getApiErrorMessage(error, 'ไม่สามารถยกเลิกได้');
              // KD3 S2357: delivery_date อาจบันทึกไปแล้วก่อนยกเลิกล้ม + สถานะอาจเปลี่ยนจากที่อื่น → โหลดใหม่
              reloadRentalView();
              showAlert('เกิดข้อผิดพลาด', msg, 'error');
              throw reportedError(msg);
            }
          }}
        />
      )}

      {/* ถามสภาพของก่อนบันทึกรับคืนของค้าง — ไม่มีค่าตั้งต้น (ของชำรุดต้องไม่กลับเข้าคลังเงียบ ๆ) */}
      <ReceiveConditionModal
        isOpen={recordReturnsOpen}
        onClose={() => setRecordReturnsOpen(false)}
        targetLabel={`อุปกรณ์ที่ยังค้าง ${outstandingReturns?.outstanding_count || 0} รายการ`}
        isBulk
        title="บันทึกรับคืนอุปกรณ์ที่ค้าง"
        bulkHint={
          <>
            สภาพที่เลือกจะใช้กับ<strong>ของค้างทุกชิ้นของสัญญานี้</strong> — ถ้าสภาพไม่เหมือนกัน
            ให้บันทึกทีละชิ้นที่เมนู "ติดตามการคืนอุปกรณ์"
          </>
        }
        submitting={recordingReturns}
        onConfirm={submitRecordReturns}
      />

      {/* Fix ก (ก-2): ปิดใบแจ้งยกเลิกทันทีเมื่อของกลับครบ — เปิดเองจาก banner ด้านบน หรือ auto-open
          หลังบันทึกรับคืนของค้างสำเร็จพอดี (submitRecordReturns) */}
      <FinalizeCancellationModal
        isOpen={finalizeModalOpen}
        onClose={() => setFinalizeModalOpen(false)}
        status={finalizeStatus}
        onFinalized={(result) => {
          setFinalizeModalOpen(false);
          showAlert('สำเร็จ', result.message, 'success');
          reloadRentalView();
        }}
        onRejected={() => {
          // BUG-S2241-001: ห้าม reloadRentalView() ตรงนี้ — loadRentalData() สลับหน้าเป็น loading
          // → โมดัล remount แล้วข้อความ error ที่ server ตอบหายทุกครั้ง (ผู้ใช้กดแล้วไม่เห็นอะไรเลย)
          // โหลดเฉพาะสถานะปิดใบ + การ์ดของค้าง พอให้แบนเนอร์/โมดัลตรงความจริง
          if (!rental) return;
          rentalService.getFinalizeCancellation(rental.id)
            .then(setFinalizeStatus)
            .catch(() => { /* โมดัลแสดงข้อความของ server อยู่แล้ว */ });
          loadOutstandingReturns();
        }}
      />

      {/* Invoice/Payment modals removed - will be replaced with Company-level system */}
      {/*
      {rental && (
        <InvoiceListModal
          isOpen={showInvoiceListModal}
          onClose={() => setShowInvoiceListModal(false)}
          rentalId={rental.id}
          rentalCode={rental.code || rental.rental_number}
          monthlyFee={rental.monthly_fee}
        />
      )}

      {rental && (
        <PaymentListModal
          isOpen={showPaymentListModal}
          onClose={() => setShowPaymentListModal(false)}
          rentalId={rental.id}
          rentalCode={rental.code || rental.rental_number}
        />
      )}
      */}
    </Layout>
  );
};

export default RentalViewPage;